import React, { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { API, useAuth } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Plus, Pencil, Trash2, Loader2, Users, Shield } from 'lucide-react';
import SystemBackupCard from '@/components/SystemBackupCard';

const ROLES = ['Super Admin', 'Admin', 'Operator', 'Intern', 'Viewer'];

const emptyUser = {
  name: '',
  email: '',
  skill_expertise: '',
  role: 'Viewer',
};

const AdminPanel = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentItem, setCurrentItem] = useState(emptyUser);
  const [submitting, setSubmitting] = useState(false);

  const fetchData = async () => {
    try {
      const res = await fetch(`${API}/admin/users`, { credentials: 'include' });
      if (res.ok) {
        setUsers(await res.json());
      } else if (res.status === 403) {
        toast.error('You do not have permission to access this page');
      }
    } catch (e) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser?.role === 'Admin' || currentUser?.role === 'Pollen Dots User' || currentUser?.role === 'Super Admin') {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [currentUser]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (editMode) {
        const res = await fetch(`${API}/admin/users/${currentItem.user_id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            name: currentItem.name,
            skill_expertise: currentItem.skill_expertise,
            role: currentItem.role,
          }),
        });

        if (res.ok) {
          toast.success('User updated');
          setDialogOpen(false);
          fetchData();
        } else {
          toast.error('Update failed');
        }
      } else {
        const res = await fetch(`${API}/admin/users`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(currentItem),
        });

        if (res.ok) {
          toast.success('User created');
          setDialogOpen(false);
          setCurrentItem(emptyUser);
          fetchData();
        } else {
          const err = await res.json();
          toast.error(err.detail || 'Create failed');
        }
      }
    } catch (e) {
      toast.error('An error occurred');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (user) => {
    setCurrentItem(user);
    setEditMode(true);
    setDialogOpen(true);
  };

  const handleDelete = async (userId) => {
    if (userId === currentUser?.user_id) {
      toast.error('You cannot delete your own account');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      const res = await fetch(`${API}/admin/users/${userId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (res.ok) {
        toast.success('User deleted');
        fetchData();
      } else {
        toast.error('Delete failed');
      }
    } catch (e) {
      toast.error('An error occurred');
    }
  };

  const openNewDialog = () => {
    setCurrentItem(emptyUser);
    setEditMode(false);
    setDialogOpen(true);
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getRoleColor = (role) => {
    const colors = {
      Admin: 'bg-red-100 text-red-800',
      'Super Admin': 'bg-red-100 text-red-800',
      'Pollen Dots User': 'bg-amber-100 text-amber-800',
      'Operator': 'bg-blue-100 text-blue-800',
      'Intern': 'bg-gray-100 text-gray-700',
      Research: 'bg-blue-100 text-blue-800',
      Engineering: 'bg-purple-100 text-purple-800',
      Compliance: 'bg-green-100 text-green-800',
      Operations: 'bg-amber-100 text-amber-800',
      Viewer: 'bg-gray-100 text-gray-800',
    };
    return colors[role] || 'bg-gray-100 text-gray-800';
  };

  if (currentUser?.role !== 'Admin' && currentUser?.role !== 'Pollen Dots User' && currentUser?.role !== 'Super Admin') {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center h-[60vh] text-center">
          <Shield className="w-16 h-16 text-muted-foreground/50 mb-4" />
          <h2 className="text-2xl font-bold text-foreground mb-2">Access Denied</h2>
          <p className="text-muted-foreground">You need Admin privileges to access this page.</p>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="admin-panel-page">
        {currentUser?.role === 'Super Admin' && <SystemBackupCard />}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight">User Management</h1>
            <p className="text-muted-foreground mt-1">Manage team members and their roles</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={openNewDialog} data-testid="add-user-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add User
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>{editMode ? 'Edit' : 'Create'} User</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    value={currentItem.name}
                    onChange={(e) => setCurrentItem({ ...currentItem, name: e.target.value })}
                    required
                    data-testid="input-name"
                  />
                </div>

                {!editMode && (
                  <>
                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={currentItem.email}
                        onChange={(e) => setCurrentItem({ ...currentItem, email: e.target.value })}
                        required
                        data-testid="input-email"
                      />
                      <p className="text-xs text-muted-foreground mt-1">User signs in via Google with this email — no password required.</p>
                    </div>
                  </>
                )}

                <div>
                  <Label htmlFor="skill_expertise">Skill / Expertise</Label>
                  <Input
                    id="skill_expertise"
                    value={currentItem.skill_expertise}
                    onChange={(e) => setCurrentItem({ ...currentItem, skill_expertise: e.target.value })}
                    placeholder="e.g., Market Research, Full-Stack Developer"
                    data-testid="input-skill"
                  />
                </div>

                <div>
                  <Label htmlFor="role">Role *</Label>
                  <Select
                    value={currentItem.role}
                    onValueChange={(v) => setCurrentItem({ ...currentItem, role: v })}
                  >
                    <SelectTrigger data-testid="select-role">
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      {ROLES.map((r) => (
                        <SelectItem key={r} value={r}>{r}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" disabled={submitting} data-testid="submit-btn">
                    {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    {editMode ? 'Update' : 'Create'} User
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Team Members ({users.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {users.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <Users className="w-12 h-12 text-muted-foreground/50 mb-4" />
                <p className="text-lg font-medium text-muted-foreground">No users yet</p>
              </div>
            ) : (
              <>
                {/* Mobile Card View */}
                <div className="sm:hidden space-y-3 p-4">
                  {users.map((user) => (
                    <div key={user.user_id} className="mobile-data-card" data-testid={`user-card-${user.user_id}`}>
                      <div className="flex items-center gap-3">
                        <Avatar className="h-10 w-10">
                          <AvatarImage src={user.picture} alt={user.name} />
                          <AvatarFallback className="bg-primary/10 text-primary text-xs">
                            {getInitials(user.name)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-foreground truncate">{user.name}</p>
                          <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                        </div>
                        <Badge className={getRoleColor(user.role)}>{user.role}</Badge>
                      </div>
                      {user.skill_expertise && (
                        <p className="text-xs text-muted-foreground">{user.skill_expertise}</p>
                      )}
                      <div className="flex gap-2 pt-1 border-t">
                        <Button variant="outline" size="sm" className="flex-1 h-10" onClick={() => handleEdit(user)}>
                          <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
                        </Button>
                        {user.user_id !== currentUser?.user_id && (
                          <Button variant="outline" size="sm" className="h-10 text-destructive" onClick={() => handleDelete(user.user_id)}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                {/* Desktop Table */}
                <div className="overflow-x-auto hidden sm:block">
                <Table className="data-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Skill/Expertise</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user.user_id} data-testid={`user-row-${user.user_id}`}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <Avatar className="h-8 w-8">
                              <AvatarImage src={user.picture} alt={user.name} />
                              <AvatarFallback className="bg-primary/10 text-primary text-xs">
                                {getInitials(user.name)}
                              </AvatarFallback>
                            </Avatar>
                            <span className="font-medium">{user.name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground">{user.email}</TableCell>
                        <TableCell>
                          <Badge className={getRoleColor(user.role)}>{user.role}</Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">{user.skill_expertise || '-'}</TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(user)} data-testid={`edit-${user.user_id}`}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          {user.user_id !== currentUser?.user_id && (
                            <Button variant="ghost" size="sm" onClick={() => handleDelete(user.user_id)} className="text-destructive" data-testid={`delete-${user.user_id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default AdminPanel;
